import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Table, Alert, Badge, Card } from "react-bootstrap";
import { FaChalkboardTeacher, FaPlus, FaTrash, FaCheck, FaTimes, FaSearch } from "react-icons/fa";

const CreateClassesModal = ({ show, onHide, schoolid, getTeachers, createBulkClasses }) => {
  const [rawClassText, setRawClassText] = useState("");
  const [parsedClasses, setParsedClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Parse options
  const [parseMode, setParseMode] = useState("newline"); // 'newline', 'comma', 'tab'

  // Grade level options - matching your Class model (only grade_level1 through grade_level4)
  const gradeLevels = [
    { value: 1, label: "Junior Infants" },
    { value: 2, label: "Senior Infants" },
    { value: 3, label: "1st Class" },
    { value: 4, label: "2nd Class" }
  ];

  useEffect(() => {
    if (show && schoolid) {
      loadTeachers();
    }
  }, [show, schoolid]);

  useEffect(() => {
    parseClassNames();
  }, [rawClassText, parseMode]);

  const loadTeachers = async () => {
    setLoading(true);
    setError("");
    try {
      const teachersResponse = await getTeachers(schoolid);
      
      const transformedTeachers = (teachersResponse || []).map(teacher => ({
        id: teacher.id,
        name: teacher.name,
        email: teacher.email,
        active: teacher.active !== false
      })).sort((a, b) => a.name.localeCompare(b.name));

      setTeachers(transformedTeachers);
    } catch (err) {
      setError("Failed to load teachers. Please try again.");
      console.error("Error loading teachers:", err);
    } finally {
      setLoading(false);
    }
  };

  const parseClassNames = () => {
    if (!rawClassText.trim()) {
      setParsedClasses([]);
      return;
    }

    let separator;
    switch (parseMode) {
      case 'comma':
        separator = ',';
        break;
      case 'tab':
        separator = '\t';
        break;
      case 'newline':
      default:
        separator = '\n';
        break;
    }

    const names = rawClassText
      .split(separator)
      .map(name => name.trim())
      .filter(name => name.length > 0)
      .map((name, index) => {
        // Auto-detect grade level from class name
        const detectedGrade = detectGradeFromName(name);
        
        return {
          id: `temp-${index}`,
          originalName: name,
          name: name,
          grade_level_number: detectedGrade || undefined,
          active: true, // Default to active
          trial_class: false,
          specialed_class: false,
          set_class: false,
          teacherId: undefined,
          classOwnerId: undefined,
          // Validation
          isValid: name.length > 0,
          validationError: name.length === 0 ? "Class name cannot be empty" : ""
        };
      });

    setParsedClasses(names);
  };

  const detectGradeFromName = (className) => {
    const name = className.toLowerCase();
    
    if (name.includes('junior') || name.includes('ji')) return 1;
    if (name.includes('senior') || name.includes('si')) return 2;
    if (name.includes('1st') || name.includes('first')) return 3;
    if (name.includes('2nd') || name.includes('second')) return 4;
    
    return null;
  };

  const updateClassField = (classId, field, value) => {
    setParsedClasses(prev => 
      prev.map(classItem => {
        if (classItem.id === classId) {
          const updated = { ...classItem, [field]: value };
          
          // Update validation
          if (field === 'name') {
            updated.isValid = value.trim().length > 0;
            updated.validationError = value.trim().length === 0 ? "Class name cannot be empty" : "";
          }
          
          return updated;
        }
        return classItem;
      })
    );
  };

  const removeClass = (classId) => {
    setParsedClasses(prev => prev.filter(classItem => classItem.id !== classId));
  };

  const addManualClass = () => {
    const newClass = {
      id: `manual-${Date.now()}`,
      originalName: "",
      name: "",
      grade_level_number: undefined,
      active: true,
      trial_class: false,
      specialed_class: false,
      set_class: false,
      teacherId: undefined,
      classOwnerId: undefined,
      isValid: false,
      validationError: "Please enter a class name"
    };
    setParsedClasses(prev => [...prev, newClass]);
  };

  const getGradeLabel = (gradeNumber) => {
    const grade = gradeLevels.find(g => g.value === gradeNumber);
    return grade ? grade.label : 'No Grade';
  };

  const handleCreateClasses = async () => {
    const validClasses = parsedClasses.filter(classItem => classItem.isValid);
    if (validClasses.length === 0) {
      setError("Please add at least one valid class.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Prepare class data for API - only include teacherId/classOwnerId if they have values
      const classesToCreate = validClasses.map(classItem => {
        const classData = {
          name: classItem.name,
          grade_level_number: classItem.grade_level_number,
          active: classItem.active,
          trial_class: classItem.trial_class,
          specialed_class: classItem.specialed_class,
          set_class: classItem.set_class
        };

        // Only include teacherId if it has a value
        if (classItem.teacherId) {
          classData.teacherId = classItem.teacherId;
        }

        // Only include classOwnerId if it has a value
        if (classItem.classOwnerId) {
          classData.classOwnerId = classItem.classOwnerId;
        }

        return classData;
      });

      console.log("Data being sent:", { classes: classesToCreate });
      // NOW PASSING SCHOOLID AS FIRST PARAMETER
      const response = await createBulkClasses(schoolid, classesToCreate);

      if (response) {
        setSuccess(`Successfully created ${validClasses.length} class(es).`);
        
        // Reset form
        setRawClassText("");
        setParsedClasses([]);
      } else {
        setError("Failed to create classes. Please try again.");
      }

    } catch (err) {
      setError(err.message || "Failed to create classes. Please try again.");
      console.error("Create classes error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setRawClassText("");
    setParsedClasses([]);
    setError("");
    setSuccess("");
    setParseMode("newline");
    onHide();
  };

  const filteredTeachers = teachers.filter(teacher => teacher.active);
  const validClassesCount = parsedClasses.filter(c => c.isValid).length;

  return (
    <Modal 
      show={show} 
      onHide={handleClose} 
      backdrop="static"
      dialogClassName="custom-modal-width"
    >
      <style>
        {`
          .custom-modal-width {
            max-width: 95% !important;
          }
          .custom-modal-width .modal-dialog {
            max-width: 95% !important;
          }
        `}
      </style>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaChalkboardTeacher className="me-2" />
          Create Classes
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body style={{ minHeight: '600px' }}>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Row className="h-100">
          {/* Column 1: Input Text Area */}
          <Col md={4} className="border-end pe-3">
            <div className="mb-4">
              <h5 className="mb-3">Paste Class Names</h5>
              
              {/* Parse Mode Selection */}
              <Form.Group className="mb-3">
                <Form.Label>Separator Type</Form.Label>
                <Form.Select 
                  value={parseMode} 
                  onChange={(e) => setParseMode(e.target.value)}
                  size="sm"
                >
                  <option value="newline">New Line</option>
                  <option value="comma">Comma</option>
                  <option value="tab">Tab</option>
                </Form.Select>
              </Form.Group>

              {/* Text Area */}
              <Form.Group className="mb-3">
                <Form.Label>Class Names</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={15}
                  placeholder={`Paste class names here...\n\nExample:\nJunior Infants A\nSenior Infants B\n1st Class Red\n2nd Class Green`}
                  value={rawClassText}
                  onChange={(e) => setRawClassText(e.target.value)}
                />
                <Form.Text className="text-muted">
                  Grade levels will be auto-detected from names
                </Form.Text>
              </Form.Group>

              <Button 
                variant="outline-primary" 
                onClick={addManualClass}
                className="w-100 mb-3"
              >
                <FaPlus className="me-1" />
                Add Single Class
              </Button>

              {parsedClasses.length > 0 && (
                <div className="text-center">
                  <Badge bg="info">
                    {parsedClasses.length} parsed ({validClassesCount} valid)
                  </Badge>
                </div>
              )}
            </div>
          </Col>

          {/* Column 2: Classes Table */}
          <Col md={8}>
            <div className="mb-3">
              <h5 className="mb-3">
                Classes to Create
                <Badge bg="primary" className="ms-2">{validClassesCount}</Badge>
              </h5>

              <div style={{ height: '520px', overflowY: 'auto' }}>
                {parsedClasses.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    Paste class names in the left panel to see them here
                  </div>
                ) : (
                  <Table striped bordered hover size="sm">
                    <thead className="sticky-top bg-light">
                      <tr>
                        <th style={{ width: '18%' }}>Class Name</th>
                        <th style={{ width: '13%' }}>Grade Level</th>
                        <th style={{ width: '7%' }}>Active</th>
                        <th style={{ width: '7%' }}>Fake</th>
                        <th style={{ width: '7%' }}>Special Ed</th>
                        <th style={{ width: '7%' }}>Set Class</th>
                        <th style={{ width: '17%' }}>Class Creator (Optional)</th>
                        <th style={{ width: '17%' }}>Class Owner (Optional)</th>
                        <th style={{ width: '6%' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedClasses.map(classItem => (
                        <tr key={classItem.id} className={!classItem.isValid ? 'table-warning' : ''}>
                          <td>
                            <Form.Control
                              size="sm"
                              value={classItem.name}
                              onChange={(e) => updateClassField(classItem.id, 'name', e.target.value)}
                              isInvalid={!classItem.isValid}
                            />
                            {!classItem.isValid && (
                              <Form.Control.Feedback type="invalid">
                                {classItem.validationError}
                              </Form.Control.Feedback>
                            )}
                          </td>
                          <td>
                            <Form.Select
                              size="sm"
                              value={classItem.grade_level_number || ''}
                              onChange={(e) => updateClassField(classItem.id, 'grade_level_number', e.target.value ? parseInt(e.target.value) : undefined)}
                            >
                              <option value="">Select Grade</option>
                              {gradeLevels.map(grade => (
                                <option key={grade.value} value={grade.value}>
                                  {grade.label}
                                </option>
                              ))}
                            </Form.Select>
                          </td>
                          <td className="text-center">
                            <Form.Check
                              type="checkbox"
                              checked={classItem.active}
                              onChange={(e) => updateClassField(classItem.id, 'active', e.target.checked)}
                              title="Class is active"
                            />
                          </td>
                          <td className="text-center">
                            <Form.Check
                              type="checkbox"
                              checked={classItem.trial_class}
                              onChange={(e) => updateClassField(classItem.id, 'trial_class', e.target.checked)}
                              title="Trial class"
                            />
                          </td>
                          <td className="text-center">
                            <Form.Check
                              type="checkbox"
                              checked={classItem.specialed_class}
                              onChange={(e) => updateClassField(classItem.id, 'specialed_class', e.target.checked)}
                              title="Special education class"
                            />
                          </td>
                          <td className="text-center">
                            <Form.Check
                              type="checkbox"
                              checked={classItem.set_class}
                              onChange={(e) => updateClassField(classItem.id, 'set_class', e.target.checked)}
                              title="Set class"
                            />
                          </td>
                          <td>
                            <Form.Select
                              size="sm"
                              value={classItem.teacherId || ''}
                              onChange={(e) => updateClassField(classItem.id, 'teacherId', e.target.value || undefined)}
                            >
                              <option value="">Select Teacher</option>
                              {filteredTeachers.map(teacher => (
                                <option key={teacher.id} value={teacher.id}>
                                  {teacher.name}
                                </option>
                              ))}
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Select
                              size="sm"
                              value={classItem.classOwnerId || ''}
                              onChange={(e) => updateClassField(classItem.id, 'classOwnerId', e.target.value || undefined)}
                            >
                              <option value="">Select Owner</option>
                              {filteredTeachers.map(teacher => (
                                <option key={teacher.id} value={teacher.id}>
                                  {teacher.name}
                                </option>
                              ))}
                            </Form.Select>
                          </td>
                          <td className="text-center">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeClass(classItem.id)}
                            >
                              <FaTrash />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100 align-items-center">
          <div className="text-muted">
            {validClassesCount > 0 && (
              <span>
                Ready to create {validClassesCount} class(es)
              </span>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={handleClose} className="me-2">
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleCreateClasses}
              disabled={validClassesCount === 0 || loading}
            >
              {loading ? "Creating Classes..." : `Create ${validClassesCount} Class(es)`}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateClassesModal;